import React, { Dispatch, SetStateAction, useContext, useState } from 'react';
import { Form } from 'react-bootstrap';
import { Row } from 'react-table';
import { useTranslation } from 'react-i18next';

import { Quality } from '../../../../Types/LotDetailQuality';

import './QualitiesSection.css';
import { LotDetailContext } from '../../../../Contexts/LotDetailContext';

export interface UsedQualityValueSelectCellProps {
    row: Row<Quality>,
    qualities: Quality[],
    setQualities: Dispatch<SetStateAction<Quality[]>>,
    canEdit?: boolean,
}

function NoValueSelectOption() {
    const { t } = useTranslation();

    return (
        <option key={`default-value-option`} className="text-secondary" value={""}>{t<string>("Application.Controls.DefaultSelect")}</option>
    );
}

function UnusedSelectOption() {
    const { t } = useTranslation();

    return (
        <option key={`default-value-option`} value={""}>{t<string>("LotDetails.Qualities.Unused")}</option>
    );
}

function UsedQualityValueSelectCell({ row, qualities, setQualities, canEdit }: UsedQualityValueSelectCellProps): React.JSX.Element {
    const { t } = useTranslation();
    const { lotDetail: { summary: { businessUnitCode } } } = useContext(LotDetailContext);

    const quality = qualities[row.index]; 
    const initialValue = quality.qualityValues.find((qv) => !qv.status?.includes("Unused"))?.testMethod ?? "";
    const [value, setValue] = useState(initialValue);

    const alwaysIncludeMethods = ["LBMLABCALC"];
    const neverIncludeMethods = ["PPK"];
    const options = quality.qualityValues
        .filter((qv) => !neverIncludeMethods.includes(qv.testMethod)
            && (alwaysIncludeMethods.includes(qv.testMethod)
                || qv.testMethod === initialValue
                || qv.resultValue !== ""))
        .map(qv => <option key={`${quality.name}-value-option-${qv.testMethod}`} value={qv.testMethod}>
            {t(`LotDetails.Qualities.${qv.testMethod}`, { context: businessUnitCode }).toString()}
        </option>);

    const saveChanges = () => {
        if (initialValue === value) {
            return;
        }

        quality.qualityValues = quality.qualityValues.map((qv) => {
            if (qv.testMethod === value) {
                var newStatus = qv.status.replace(" Unused", "");
                return { ...qv, status: newStatus, graderSelected: true };
            }

            if (qv.testMethod !== value && !qv.status.includes("Unused")) {
                return { ...qv, status: qv.status + " Unused", graderSelected: false };
            }

            return qv;
        });
        setQualities([...qualities]);
    }

    return (
        <Form.Select name={`${quality.name}-used-value-select`} className="used-value-select" value={value} onChange={(e) => setValue(e.target.value)} onBlur={saveChanges} disabled={!canEdit || !quality.isRequiredForGrading} >
            {quality.isRequiredForGrading && !value && <NoValueSelectOption />}
            {quality.isRequiredForGrading && options}
            {!quality.isRequiredForGrading && <UnusedSelectOption />}
        </Form.Select>
    );
}

export default UsedQualityValueSelectCell;